// frontend/src/pages/Company/AddUserForm.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import {
  UserPlus, Save, Loader2, X, ArrowLeft, Users,
  Mail, Phone, MapPin, Key, Shield, AlertCircle,
  Building2, ChevronLeft
} from "lucide-react";
import "./CompanyForm.css";

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const AddUserForm = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [focused, setFocused] = useState({});
  const [company, setCompany] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [userData, setUserData] = useState({
    Usercomp_sure_name: "",
    Usercomp_contact: "",
    Usercomp_contact_2: "",   // ✅ NEW
    Usercomp_contact_3: "",   // ✅ NEW
    Usercomp_address: "",
    Usercomp_email: "",
    password: "",
    confirm_password: "",
    role: "user", // Default role, can be changed to "cashier" or "sales"
    Usercomp_status: "active"
  });

  useEffect(() => {
    fetchCompanyDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const fetchCompanyDetails = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${url}/api/company/${companyId}`, {
        headers: getAuthHeaders(),
      });
      if (res.data.success) {
        setCompany(res.data.data);
      }
    } catch (err) {
      toast.error("Failed to load company details");
      navigate("/ViewCompany");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleFocus = (field) => setFocused({ ...focused, [field]: true });
  const handleBlur = (field, value) =>
    setFocused({ ...focused, [field]: value && value.length > 0 });

  const validateForm = () => {
    const newErrors = {};

    if (!userData.Usercomp_sure_name.trim()) {
      newErrors.Usercomp_sure_name = "Full name is required";
    }

    if (!userData.Usercomp_contact.trim()) {
      newErrors.Usercomp_contact = "Contact number is required";
    }

    if (!userData.Usercomp_address.trim()) {
      newErrors.Usercomp_address = "Address is required";
    }

    if (!userData.Usercomp_email.trim()) {
      newErrors.Usercomp_email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(userData.Usercomp_email)) {
      newErrors.Usercomp_email = "Email is invalid";
    }

    if (!userData.password) {
      newErrors.password = "Password is required";
    } else if (userData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (!userData.confirm_password) {
      newErrors.confirm_password = "Please confirm password";
    } else if (userData.password !== userData.confirm_password) {
      newErrors.confirm_password = "Passwords do not match";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fix the errors in the form");
      return false;
    }

    setErrors({});
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setSubmitting(true);

      const payload = {
        ...userData,
        company_id: companyId
      };

      const response = await axios.post(
        `${url}/api/company/${companyId}/users`,
        payload,
        { headers: getAuthHeaders() }
      );

      toast.success(response.data.message || "User created successfully!");

      setTimeout(() => {
        navigate(`/company/${companyId}`);
      }, 1500);

    } catch (err) {
      const errorMsg = err.response?.data?.message || "Failed to create user";
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/company/${companyId}`);
  };

  if (loading) {
    return (
      <div className="form-wrapper">
        <div className="company-form-container">
          <div className="loading-container">
            <Loader2 className="animate-spin" size={32} />
            <p>Loading company details...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="form-wrapper">
      <div className="company-form-container">
        {/* Close button */}
        <button
          className="close-btn"
          onClick={handleCancel}
          title="Close"
        >
          <X size={20} />
        </button>

        {/* Back button */}
        <div style={{ marginBottom: '20px' }}>
          <button
            onClick={handleCancel}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'none',
              border: 'none',
              color: '#121349',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              padding: '10px 0',
            }}
          >
            <ArrowLeft size={20} />
            Back to Company
          </button>
        </div>

        {/* Header */}
        <div className="form-header">
          <UserPlus size={24} />
          <h2 className="form-title">Add New User</h2>
          <p className="form-subtitle">
            Create a new user for {company?.company_name}
          </p>
        </div>

        {/* Company Info */}
        {company && (
          <div style={{
            marginBottom: '30px',
            padding: '20px',
            background: 'rgba(18, 19, 73, 0.05)',
            borderRadius: '10px',
            borderLeft: '4px solid #f89a3a'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
              <Building2 size={20} color="#121349" />
              <h4 style={{ color: '#121349', margin: 0 }}>Company Information</h4>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
              <div>
                <small style={{ color: '#666', fontWeight: '600' }}>Company Name</small>
                <p style={{ margin: '5px 0', fontWeight: '600' }}>{company.company_name}</p>
              </div>
              <div>
                <small style={{ color: '#666', fontWeight: '600' }}>Company Code</small>
                <p style={{ margin: '5px 0' }}>
                  <span className="company-code" style={{ fontSize: '12px' }}>
                    {company.company_code}
                  </span>
                </p>
              </div>
              <div>
                <small style={{ color: '#666', fontWeight: '600' }}>Business Type</small>
                <p style={{ margin: '5px 0' }}>{company.business_type?.name || "Not specified"}</p>
              </div>
            </div>
          </div>
        )}

        {/* User Form */}
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            {/* Full Name */}
            <div className={`form-field required ${focused.Usercomp_sure_name || userData.Usercomp_sure_name ? "filled" : ""} ${errors.Usercomp_sure_name ? 'error' : ''}`}>
              <input
                name="Usercomp_sure_name"
                value={userData.Usercomp_sure_name}
                onChange={handleChange}
                onFocus={() => handleFocus("Usercomp_sure_name")}
                onBlur={() => handleBlur("Usercomp_sure_name", userData.Usercomp_sure_name)}
                placeholder="Enter user's full name"
                required
              />
              <label>Full Name</label>
              {errors.Usercomp_sure_name && (
                <small className="field-error">{errors.Usercomp_sure_name}</small>
              )}
            </div>
{/* Contact Number (Phone 1) */}
<div className={`form-field required ${focused.Usercomp_contact || userData.Usercomp_contact ? "filled" : ""} ${errors.Usercomp_contact ? 'error' : ''}`}>
  <input
    name="Usercomp_contact"
    value={userData.Usercomp_contact}
    onChange={handleChange}
    onFocus={() => handleFocus("Usercomp_contact")}
    onBlur={() => handleBlur("Usercomp_contact", userData.Usercomp_contact)}
    placeholder="Enter primary phone number"
    required
  />
  <label>Phone 1 (Primary)</label>
  {errors.Usercomp_contact && <small className="field-error">{errors.Usercomp_contact}</small>}
</div>

{/* ✅ NEW: Phone 2 */}
<div className={`form-field ${focused.Usercomp_contact_2 || userData.Usercomp_contact_2 ? "filled" : ""}`}>
  <input
    name="Usercomp_contact_2"
    value={userData.Usercomp_contact_2}
    onChange={handleChange}
    onFocus={() => handleFocus("Usercomp_contact_2")}
    onBlur={() => handleBlur("Usercomp_contact_2", userData.Usercomp_contact_2)}
    placeholder="Enter secondary phone number (optional)"
  />
  <label>Phone 2 (Optional)</label>
</div>

{/* ✅ NEW: Phone 3 */}
<div className={`form-field ${focused.Usercomp_contact_3 || userData.Usercomp_contact_3 ? "filled" : ""}`}>
  <input
    name="Usercomp_contact_3"
    value={userData.Usercomp_contact_3}
    onChange={handleChange}
    onFocus={() => handleFocus("Usercomp_contact_3")}
    onBlur={() => handleBlur("Usercomp_contact_3", userData.Usercomp_contact_3)}
    placeholder="Enter tertiary phone number (optional)"
  />
  <label>Phone 3 (Optional)</label>
</div>

            {/* Email Address */}
            <div className={`form-field required ${focused.Usercomp_email || userData.Usercomp_email ? "filled" : ""} ${errors.Usercomp_email ? 'error' : ''}`}>
              <div className="input-with-icon">
                <input
                  type="email"
                  name="Usercomp_email"
                  value={userData.Usercomp_email}
                  onChange={handleChange}
                  onFocus={() => handleFocus("Usercomp_email")}
                  onBlur={() => handleBlur("Usercomp_email", userData.Usercomp_email)}
                  placeholder="Enter email address"
                  required
                />
                <Mail size={16} className="input-icon" />
              </div>
              <label>Email Address</label>
              {errors.Usercomp_email && (
                <small className="field-error">{errors.Usercomp_email}</small>
              )}
            </div>

            {/* Role Selection */}
            <div className={`form-field required ${focused.role || userData.role ? "filled" : ""}`}>
              <div className="select-wrapper">
                <select
                  name="role"
                  value={userData.role}
                  onChange={handleChange}
                  onFocus={() => handleFocus("role")}
                  onBlur={() => handleBlur("role", userData.role)}
                  required
                >
                  <option value="user">User (General)</option>
                  <option value="sales">Sales Staff</option>
                  <option value="cashier">Cashier</option>
                  <option value="purchase">Purchase Staff</option>
                </select>
                <Shield size={16} className="select-icon" />
              </div>
              <label>User Role</label>
              <small className="field-hint">
                {userData.role === 'admin' && 'Full system access'}
                {userData.role === 'sales' && 'Can create sales orders & invoices'}
                {userData.role === 'cashier' && 'Can process payments & cash transactions'}
                {userData.role === 'purchase' && 'Can create purchase orders'}
                {userData.role === 'user' && 'General user access'}
              </small>
            </div>

            {/* Password */}
            <div className={`form-field required ${focused.password || userData.password ? "filled" : ""} ${errors.password ? 'error' : ''}`}>
              <div className="input-with-icon">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={userData.password}
                  onChange={handleChange}
                  onFocus={() => handleFocus("password")}
                  onBlur={() => handleBlur("password", userData.password)}
                  placeholder="Enter password (min 6 characters)"
                  required
                />
                <Key size={16} className="input-icon" />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
              <label>Password</label>
              {errors.password && (
                <small className="field-error">{errors.password}</small>
              )}
            </div>

            {/* Confirm Password */}
            <div className={`form-field required ${focused.confirm_password || userData.confirm_password ? "filled" : ""} ${errors.confirm_password ? 'error' : ''}`}>
              <div className="input-with-icon">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirm_password"
                  value={userData.confirm_password}
                  onChange={handleChange}
                  onFocus={() => handleFocus("confirm_password")}
                  onBlur={() => handleBlur("confirm_password", userData.confirm_password)}
                  placeholder="Confirm password"
                  required
                />
                <Key size={16} className="input-icon" />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? "Hide" : "Show"}
                </button>
              </div>
              <label>Confirm Password</label>
              {errors.confirm_password && (
                <small className="field-error">{errors.confirm_password}</small>
              )}
            </div>

            {/* Address */}
            <div className={`form-field required ${focused.Usercomp_address || userData.Usercomp_address ? "filled" : ""} ${errors.Usercomp_address ? 'error' : ''}`}>
              <div className="input-with-icon">
                <textarea
                  name="Usercomp_address"
                  value={userData.Usercomp_address}
                  onChange={handleChange}
                  onFocus={() => handleFocus("Usercomp_address")}
                  onBlur={() => handleBlur("Usercomp_address", userData.Usercomp_address)}
                  placeholder="Enter user's full address"
                  rows="3"
                  required
                />
                <MapPin size={16} className="input-icon textarea-icon" />
              </div>
              <label>Address</label>
              {errors.Usercomp_address && (
                <small className="field-error">{errors.Usercomp_address}</small>
              )}
            </div>

            {/* Status */}
            <div className={`form-field ${focused.Usercomp_status || userData.Usercomp_status ? "filled" : ""}`}>
              <select
                name="Usercomp_status"
                value={userData.Usercomp_status}
                onChange={handleChange}
                onFocus={() => handleFocus("Usercomp_status")}
                onBlur={() => handleBlur("Usercomp_status", userData.Usercomp_status)}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <label>Status</label>
            </div>
          </div>

          {/* Role Explanation */}

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Creating User...
                </>
              ) : (
                <>
                  Create User
                  <UserPlus className="ml-2" size={16} />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUserForm;
